import { ApiError } from '@/api/errors';
import { openapiClient } from '@/api/openapi-client';
import { queryClient } from '@/api/query-client';
import { UserContextShapeError, fetchUserContext } from '@/api/user-context';
import { useSyncUserLanguage } from '@/hooks/useSyncUserLanguage';
import { identifyUmami } from '@/lib/analytics/umami-client';
import { setFaroUser } from '@/lib/observability/faro-client';
import { useAuthStore } from '@/stores/auth.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { Button } from 'framework7-react';
import { Settings } from 'luxon';
import { useEffect, useState } from 'react';

interface BootstrapGateProps {
  restaurantId: string;
  children: React.ReactNode;
}

/**
 * Wraps the authenticated app. Fetches user context and restaurant config
 * before rendering children. Reruns whenever restaurantId changes (restaurant switch).
 *
 * Login and select-restaurant pages are outside this gate and render freely.
 */
function BootstrapGate({ restaurantId, children }: BootstrapGateProps) {
  const restaurantConfig = useRestaurantStore((s) => s.restaurantConfig);
  const { setUserContext, setRestaurantConfig, deselectRestaurant, markRestaurantDenied } =
    useRestaurantStore();
  const logout = useAuthStore((s) => s.logout);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useSyncUserLanguage();

  // Apply the restaurant timezone globally before any child renders.
  if (restaurantConfig) {
    Settings.defaultZone = restaurantConfig.timezone;
  }

  useEffect(() => {
    setError(null);

    let cancelled = false;

    async function bootstrap() {
      try {
        const [ctx, restaurantResult] = await Promise.all([
          fetchUserContext(),
          openapiClient.GET('/api/restaurants/{restaurantId}', {
            params: { path: { restaurantId } },
          }),
        ]);

        if (cancelled) return;

        // Publish this fresh context to the ['auth','me'] cache the picker
        // reads. Without it the two disagree - the gate sees the revoked access
        // and deselects, while the picker still holds a <10min-old cache that
        // lists the restaurant and auto-selects it again - a mount ping-pong
        // that hammers the API a few times per second.
        queryClient.setQueryData(['auth', 'me'], ctx);

        // Stamp the identity onto Faro before anything else can fail. This is the
        // only place that holds both the fresh context and the current selection,
        // and it runs BEFORE the guards below so a refused restaurant is reported
        // with the roles that refused it.
        const worker = ctx.workers.find((w) =>
          w.restaurants.some((r) => r.restaurantId === restaurantId),
        );
        setFaroUser({
          userId: ctx.userId,
          systemRole: ctx.systemRole,
          franchiseId: worker?.franchiseId,
          franchiseRole: worker?.franchiseRole,
          restaurantId,
          restaurantRole: worker?.restaurants.find((r) => r.restaurantId === restaurantId)?.role,
        });
        identifyUmami({
          userId: ctx.userId,
          restaurantId,
          restaurantRole: worker?.restaurants.find((r) => r.restaurantId === restaurantId)?.role,
        });

        // Guard against a stale persisted selection: a different user logging
        // in on this browser, a deleted restaurant, or rotated IDs. If the
        // selected restaurant isn't one this account can actually access, drop
        // the selection and fall back to the picker (which auto-selects when
        // the user has exactly one restaurant) instead of firing forbidden
        // requests for someone else's restaurant.
        const accessibleRestaurantIds = new Set(
          ctx.workers.flatMap((w) => w.restaurants.map((r) => r.restaurantId)),
        );
        if (!accessibleRestaurantIds.has(restaurantId)) {
          setUserContext(ctx);
          markRestaurantDenied(restaurantId);
          deselectRestaurant();
          return;
        }

        // 403 means the API disagrees with /auth/me: the context lists this
        // restaurant but authorization refuses it (revoked membership caught
        // mid-flight, or a role the ability rules don't grant `read Restaurant`
        // for). Retrying can't fix that, so treat it like the guard above -
        // remember the refusal and fall back to the picker, which will skip it.
        if (restaurantResult.response.status === 403) {
          setUserContext(ctx);
          markRestaurantDenied(restaurantId);
          deselectRestaurant();
          return;
        }

        // The session died mid-bootstrap. `authFetch` has already refreshed,
        // retried, and — still refused — logged out, which flips App to /login
        // and unmounts this gate. Nothing left to report or recover.
        if (restaurantResult.response.status === 401) return;

        if (!restaurantResult.data) {
          console.error(
            '[BootstrapGate] restaurant fetch status:',
            restaurantResult.response.status,
            restaurantResult.error,
          );
          setError(
            restaurantResult.response.status === 404
              ? "This restaurant doesn't exist anymore - pick another one."
              : 'Failed to load restaurant configuration.',
          );
          return;
        }

        setUserContext(ctx);
        setRestaurantConfig(restaurantResult.data);
      } catch (err) {
        // Same as the 401 check above, for the /auth/me half of the pair: an
        // expired session is an ordinary end-of-session, not a bootstrap
        // failure. It used to reach the lines below, which console.error'd it
        // into Faro as "[BootstrapGate] bootstrap failed" and set a *network*
        // error message for what is really "please sign in again".
        if (err instanceof ApiError && err.status === 401) return;
        console.error('[BootstrapGate] bootstrap failed:', err);
        if (cancelled) return;
        setError(
          err instanceof UserContextShapeError
            ? 'Unexpected response from the server. Please try again or contact support.'
            : 'Unable to connect. Please check your connection.',
        );
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [
    restaurantId,
    retryNonce,
    setUserContext,
    setRestaurantConfig,
    deselectRestaurant,
    markRestaurantDenied,
  ]);

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100dvh',
          gap: 16,
          padding: '0 32px',
        }}
      >
        <i className="f7-icons" style={{ fontSize: 48, color: '#ff3b30' }}>
          exclamationmark_triangle
        </i>
        <p style={{ color: '#ff3b30', margin: 0, textAlign: 'center' }}>{error}</p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button fill round onClick={() => setRetryNonce((n) => n + 1)}>
            Retry
          </Button>
          <Button round onClick={deselectRestaurant}>
            Choose another restaurant
          </Button>
          {/* Last-resort escape: most non-404 failures here are an expired/invalid
              session, which Retry and the picker can't fix - only a fresh login
              can. logout() clears auth + selection, flipping the app to /login. */}
          <Button round color="red" onClick={logout}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  if (!restaurantConfig) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100dvh',
        }}
      >
        <div className="preloader" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  return <>{children}</>;
}

export default BootstrapGate;
